import { parse } from 'qs'
import actionTypes from 'redux/actions/actionTypes'
import findFilter from 'helpers/filter/findFilter'
import {findItemInCategory} from 'helpers/filter/findInCategory'
import postMessage, { postInFrames } from 'helpers/postMessage/nearpodPostMessage'
import googleDriveActions from 'redux/actions/googleDriveActions'
import getGoogleDriveSlidesList, {getActivitySize} from 'views/google_slides/helpers/slides_list'
import shouldPreventSlideChangeSelector from 'redux/selectors/interactive_videos/interactive_video_should_prevent_slide_change_selector'
import externalSelector from 'redux/selectors/external/external_selector'
import confirmTypes from 'helpers/enums/confirmTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import errorCodes from 'helpers/enums/errorCodes'
import { localize } from 'helpers/translator'
import onboardingActivityActions from 'redux/actions/onboardingActivityActions'
import quickLaunchActions from 'redux/actions/quickLaunchActions'
import sessionActions from 'redux/actions/sessionActions'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'
import { cacophonyTypeToStreamMode } from 'helpers/videoStreamMode'
import ivImmersiveReaderSelector from 'redux/selectors/interactive_videos/interactive_video_immersive_reader_selector'
import config from 'config'
import qsStudentJoinParamsSelector from 'redux/selectors/qs/qs_student_join_params_selector'
import whiteboardModes from 'helpers/enums/whiteboardModes'

const postMessageTypes = [
	actionTypes.THEMES_CHANGE_PAYWALL,
	actionTypes.THEMES_CHANGE_SUCCESS,
]

const postFiltersUpdateMessage = (store, action_type) => {
	const state = store.getState()
	const categories = state.store.currentTab === '0'
		? state.store.categories
		: state.store.standard
	let strandId = ''
	const friendlyText = state.store.confirmedFilters.map(entityUID => findFilter(categories, entityUID))
		.reduce((accumulator, item) => {
			accumulator += item.friendlyText
			accumulator += '-'
			return accumulator
		}, '')
	if (state.store.currentTab === '1') {
		state.store.confirmedFilters.forEach(entityUID => {
			if (findItemInCategory(categories[0], entityUID)) {
				strandId = entityUID
			}
		})
	}
	const confirmedFiltersRenamed = state.store.confirmedFilters.map(item => {
		if (item.indexOf('--C') !== -1) {
			return item.substr(0, item.indexOf('--C'))
		} else {
			return item
		}
	})

	const searchData = {
		filters: confirmedFiltersRenamed,
		friendlyText,
		query: state.store.query,
		strandId,
	}
	postMessage(action_type, searchData)
}

const postPollComplete = (store, action) => {
	let canPostPollComplete = true
	if (action.type === actionTypes.STUDENT_SUBMIT_POLL_ANSWERS) {
		canPostPollComplete = !(action.slide_data.answers.length === 0)
	}
	if (canPostPollComplete) {
		postMessage(action.type)
	} else if (action.type === actionTypes.STUDENT_SUBMIT_POLL_ANSWERS
		&& action.slide_data.isSubmissionRequired
		&& !action.shouldEnforceSubmission) {
		postMessage(actionTypes.STUDENT_SUBMIT_SKIPPED)
	}
}

const postQuizComplete = (store, action) => {
	let canPostQuizComplete = true
	if (action.type === actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ) {
		canPostQuizComplete = action.sessionType === sessionTypes.EMBED ||
			!action.slide_data.questions.find(question => question.selected_answers.length === 0)
	}
	if (canPostQuizComplete) {
		postMessage(action.type)
	} else if (action.type === actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ
		&& action.slide_data.isSubmissionRequired
		&& !action.shouldEnforceSubmission) {
		postMessage(actionTypes.STUDENT_SUBMIT_SKIPPED)
	}
}

const postOeqComplete = (store, action) => {
	let canPostOeqComplete = true
	if (action.type === actionTypes.STUDENT_SUBMIT_OEQ_ANSWER) {
		canPostOeqComplete = !(action.slide_data.answers[0].text === '' && !action.slide_data.audioClientUid)
	}
	if (canPostOeqComplete) {
		postMessage(action.type)
	} else if (action.type === actionTypes.STUDENT_SUBMIT_OEQ_ANSWER
		&& action.slide_data.isSubmissionRequired
		&& !action.shouldEnforceSubmission) {
		postMessage(actionTypes.STUDENT_SUBMIT_SKIPPED)
	}
}

const postDrawItComplete = (store, action) => {
	if (!action.slide_data.answers.length && action.slide_data.isSubmissionRequired) {
		if (!action.shouldEnforceSubmission) {
			postMessage(actionTypes.STUDENT_SUBMIT_SKIPPED)
		}
	} else {
		postMessage(action.type)
	}
}

const postInteractiveVideoComplete = (store, action) => {
	const shouldPreventSlideChange = shouldPreventSlideChangeSelector(store.getState())
	if (!action.shouldEnforceSubmission || !shouldPreventSlideChange) {
		postMessage(action.type)
	}
}

const postStudentSubmitFailed = action => {
	if (action.error.error_code !== localErrorCodes.DRAWIT_SUBMISSION_REQUIRED) {
		postMessage(action.type)
	}
}

const postStudentDrawItUnsupportedFileError = action => {
	const payload = { msg: action.msg}
	postMessage(action.type, payload)
}

const postActivityTimerReady = (store, action) => {
	const state = store.getState()
	const slide_data = state.student.slides[action.slide_uid]
	// Verify that the action has the neccesary data to initialize the timer
	if (action.timer) {
		// Verify that the timer status is not set
		if (!slide_data.timer.state) {
			postMessage(action.type)
		}
	}
}

const postActivityTimerStarted = action => {
	postMessage(action.type)
}

const postQuizSlideChange = action => {
	postMessage(action.type, {index: action.newIndex})
}

const checkSlideReady = (store, action) => {
	if (!action.slide_data.hasChanged) {
		postMessage(action.type)
	}
}

const postExtensionSessionLaunched = action => {
	postMessage(action.type, {
		assignmentTitle: action.classroomData.name,
		assignmentMaterial: action.classroomData.urlWeb,
	}, 'parent')
}

const postGoogleSlidesActivityCreate = action => {
	const size = getActivitySize(action.id)
	postMessage(action.type, {
		id: action.id,
		size,
	}, 'parent')
}

const postGoogleSlidesInitializeActivityCancel = () => {
	postInFrames(actionTypes.GOOGLE_SLIDES_ACTIVITY_CANCEL, {}, 'top')
}

const postGoogleSlidesInitializeActivitySave = action => {
	if (!action.params.slideExternalId || action.params.shouldUpdatePlaceholder) {
		const activities = getGoogleDriveSlidesList()
		const activity = activities
			.find(matchActivity => (matchActivity.id === action.params.slideTypeId))

		const overlay = activity ? activity.overlay : ''

		if (!action.params.slideExternalId) {
			const payload = {...action.params, overlay}

			postInFrames(actionTypes.GOOGLE_SLIDES_ACTIVITY_SAVE, payload, 'top')
		} else {
			const payload = { slideExternalId: action.params.slideExternalId, imageUrl: [overlay] }
			postInFrames(actionTypes.GOOGLE_SLIDES_CHANGE_SLIDE_CONTENT, payload, 'top')
		}
	}
}

const postGoogleSlidesActivitySaved = action => {
	const params = {
		icon: action.icon,
		slideExternalId: action.slideExternalId,
		slideTypeId: action.slideTypeId,
	}
	postInFrames(actionTypes.GOOGLE_SLIDES_ACTIVITY_SAVED, params, 'top')
}

const postGoogleSlidesEditSlide = () => {
	postInFrames(actionTypes.GOOGLE_SLIDES_ACTIVITY_EDIT, {}, 'top')
}
const postGoogleSlidesEditSlideSuccess = action => {
	const size = getActivitySize(action.payload.slide_external.slideTypeId)
	const payload = {
		...action.payload,
		size,
	}
	postInFrames(
		actionTypes.GOOGLE_SLIDES_GET_EXTERNAL_SLIDE_DATA_FOR_EDIT_SUCCESS,
		payload,
		'top'
	)
}

const postGetExternalSlideDataForEditFailed = action => {
	switch (action.serverError.error_code) {
		case errorCodes.GOOGLE_SLIDES_EDIT_NON_NEARPOD_SLIDE:
			postGoogleSlidesShowConfirm({confirmType: confirmTypes.GOOGLE_SLIDES_EDIT_NON_NEARPOD_SLIDE, title: localize('googleSlides_confirm_addFeature_title')})
			break
		case errorCodes.GOOGLE_SLIDES_EDIT_AUDIO_SLIDE:
			postGoogleSlidesShowConfirm({confirmType: confirmTypes.GOOGLE_SLIDES_EDIT_AUDIO_SLIDE, title: localize('googleSlides_confirm_editAudio_title')})
			break
		default:
			break
	}
}

const postGoogleSlidesShowConfirm = action => {
	const url = `${config.www}google-slides/confirm?type=${action.confirmType}`
	const size = {
		width: 612,
		height: 195,
	}

	const title = action.title || ' '

	postGoogleSlidesOpenModal({url, size, title})
}

const postGoogleSlidesOpenModal = action => {
	const payload = {
		url: action.url,
		size: action.size,
		title: action.title,
	}
	postInFrames(actionTypes.GOOGLE_SLIDES_OPEN_MODAL, payload, 'top')
}

const postGoogleSlidesInitializeSuccess = action => {
	const invalidSlides = action.response.result.data.sourceInfo.payload.invalidSlides

	let invalidOverlays = []
	if (invalidSlides && invalidSlides.length > 0) {
		//for now this is only for video slides (slideTypeId = 21) and audio slides (slideTypeId = 1)
		invalidOverlays = invalidSlides.map(slide => {
			const activities = getGoogleDriveSlidesList()
			const activity = activities
				.find(matchActivity => (matchActivity.id === slide.slideTypeId))

			return {
				slideExternalId: slide.slideExternalId,
				url: activity && activity.errorOverlay ? activity.errorOverlay : '',
			}

		}).filter(slide => slide.url !== '')
		//leaving this postmessage for older add-on versions,
		//should be removed once we release the new add-on
		postInFrames(actionTypes.GOOGLE_SLIDES_INVALID_SLIDES, invalidOverlays, 'top')
	}
	//new add-on version will only listen to this postmessage
	postInFrames(
		actionTypes.GOOGLE_SLIDES_INITIALIZE_SUCCESS,
		{ slidePlaceholders: invalidOverlays },
		'top'
	)
}

function postGoogleSlidesSideBarLoaded() {
	postInFrames(actionTypes.GOOGLE_SLIDES_SIDEBAR_LOADED, {}, 'top')
}

function postLogInLoadedInGoogleSlides(action) {
	const params = parse(action.locationSearch)

	if (params.from_google_slides === '1') {
		postGoogleSlidesSideBarLoaded()
	}
}

const postActivityScreenshotObtained = (store, action) => {
	const slide = externalSelector(store.getState(), action.icon)
	if (slide) {
		postInFrames(actionTypes.GOOGLE_SLIDES_SCREENSHOT_OBTAINED, slide, 'top')
	}
}

function postConvertToDrawItGetThumbnailSuccess(action) {
	postInFrames(
		actionTypes.GOOGLE_SLIDES_CONVERT_TO_DRAW_IT_GET_THUMBNAIL_SUCCESS,
		{ activityData: action.activityData },
		'top'
	)
}

function postSSOSuccess(action) {
	postMessage(
		'SSO_SUCCESS',
		{ status: action.params.status },
		'opener'
	)
}

const postLeadSubmit = action => {
	if (action.sessionType !== sessionTypes.TEACHER_STUDENT_VIEW) {
		postMessage(action.type, {name: action.name, optionalName: action.optionalName})
	}
}

const postLeadSubmitSuccess = action => {
	postMessage(action.type, {isHomework: action.sessionType === sessionTypes.HOMEWORK})
}

const postLeadSubmitFailed = action => {
	postMessage(action.type, {errorCode: action.data.error_code})
}

const handleReadOnlyNavigation = (store, action) => {
	if (!action.link.url) {
		const link = {
			...action.link,
		}
		if (link.pageObjectId) {
			const state = store.getState()
			const metadata = state.lessonSession.metadata
			const slideId = Object.keys(metadata.slides)
				.find(slideId => metadata.slides[slideId].data.payload.objectId === link.pageObjectId)
			link.screenName = slideId || ''
		}
		postMessage(actionTypes.READ_ONLY_NAVIGATE, link)
	}
}

const postExternalImmersiveReaderChunks = (store, action) => {

	const isImmersiveReaderEnabled = store.getState().interactiveVideo.isExternalImmersiveReaderEnabled
	if (isImmersiveReaderEnabled && window.webkit) {
		const chunks = ivImmersiveReaderSelector(store.getState(), action.activityUid)
		const myJSON = JSON.stringify({ text: chunks })
		window.webkit.messageHandlers.nearpodiVideoIR.postMessage(myJSON)
	}
}

const postStudentJoinValidPincode = (store, action) => {
	const studentJoinParams = qsStudentJoinParamsSelector(store.getState())
	if (studentJoinParams.isSessionPreview) {
		postInFrames(action.type, {}, 'top')
	}
}

const postWhiteboardUpdate = action => {
	let whiteboardMessageType, mode
	if (action.type === actionTypes.WHITEBOARD_FETCHED) {
		mode = action.whiteboard.mode
		whiteboardMessageType = action.whiteboard.mode === whiteboardModes.NONE
			? actionTypes.WHITEBOARD_DISABLE
			: actionTypes.WHITEBOARD_ENABLE
	} else {

		mode = [actionTypes.WHITEBOARD_DISABLE, actionTypes.WHITEBOARD_DISABLED].includes(action.type)
			? whiteboardModes.NONE
			: action.mode
		whiteboardMessageType = action.type
	}
	postMessage(whiteboardMessageType, {mode})
}

const handleStudentInitializeLead = action => {
	if (action.session.isSessionPreview) {
		postLeadSubmit({
			type: actionTypes.STUDENT_LEAD_SUBMIT,
			sessionType: action.session.sessionType,
			name: action.slide.complementaryLeadData.firstField,
			optionalName: action.slide.complementaryLeadData.secondField,
		})
	}
}

const postMessageMiddleware = store => {
	window.addEventListener('message', function(e) {
		if (e.data.type === 'nearpodMessage') {
			switch (e.data.action_type) {
				case actionTypes.GOOGLE_SLIDES_SLIDE_EXTERNAL_ID_OBTAINED:
					store.dispatch(googleDriveActions.googleSlidesExternalIdObtained(e.data))
					break
				case actionTypes.GOOGLE_SLIDES_ACTIVITY_EDIT_EXTERNAL_ID_OBTAINED:
					store.dispatch(googleDriveActions.googleSlidesActivityEditExternalIdObtained(e.data))
					break
				case actionTypes.GOOGLE_SLIDES_ACTIVITY_SAVED:
					store.dispatch(googleDriveActions.googleSlidesActivitySaveFinished(e.data))
					break
				case actionTypes.ONBOARDING_ACTIVITY_DEMO_CARD_POSTED:
					store.dispatch(onboardingActivityActions.demoCardPosted(e.data.text))
					break
				case actionTypes.GOOGLE_SLIDES_CHANGE_CURRENT_SLIDE:
					store.dispatch(googleDriveActions.googleCurrentSlideChanged(e.data.payload.slideExternalId))
					break
				case actionTypes.QUICK_LAUNCH_COLLABORATE_SAVE_ACTIVITY_SUCCESS:
					store.dispatch(quickLaunchActions.createCollaborateActivitySaveSuccess())
					break
				case actionTypes.QUICK_LAUNCH_COLLABORATE_CANCEL_ACTIVITY:
					store.dispatch(
						quickLaunchActions.createCollaborateActivityCancel()
					)
					break
				case actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE:
					store.dispatch(
						sessionActions.hideStudentNamesUpdate(e.data.hideStudentNames)
					)
					break
				case actionTypes.SESSION_SETTINGS_CACOPHONY_TYPE_UPDATE:
					store.dispatch(
						sessionActions.streamModeUpdate(
							cacophonyTypeToStreamMode(e.data.cacophonyType)
						)
					)
					break
			}
		}
	})

	return next => action => {

		if (postMessageTypes.indexOf(action.type) > -1) {
			const payload = {...action}
			delete payload['type']
			postMessage(action.type, payload)
		}
		switch (action.type) {
			case actionTypes.STUDENT_TIMER_UPDATED:
				postActivityTimerReady(store, action)
				break
		}
		next(action)
		switch (action.type) {
			case actionTypes.STORE_FILTERS_SEARCH:
			case actionTypes.STORE_QUERY_SEARCH:
			case actionTypes.STORE_CLEAR_SELECTED_FILTERS:
			case actionTypes.STORE_UPDATE_FILTERS:
				postFiltersUpdateMessage(store, action.type)
				break
			case actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_GO_TO_MY_LIBRARY:
				postMessage(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_GO_TO_MY_LIBRARY_SUCCESS)
				break
			case actionTypes.STUDENT_SUBMIT_POLL_ANSWERS:
			case actionTypes.STUDENT_SUBMIT_POLL_CONFIRMED:
				postPollComplete(store, action)
				break
			case actionTypes.STUDENT_SLIDE_CHECK_READY:
				checkSlideReady(store, action)
				break
			case actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ:
			case actionTypes.STUDENT_SUBMIT_QUIZ_CONFIRMED:
			case actionTypes.STUDENT_QUIZ_FIRST_SLIDE_REACHED:
				postQuizComplete(store, action)
				break
			case actionTypes.STUDENT_SUBMIT_OEQ_ANSWER:
			case actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED:
				postOeqComplete(store, action)
				break
			case actionTypes.STUDENT_SUBMIT_DRAWIT_CONFIRMED:
				postDrawItComplete(store, action)
				break
			case actionTypes.INTERACTIVE_VIDEO_SLIDE_CHANGED:
				postInteractiveVideoComplete(store, action)
				break
			case actionTypes.STUDENT_SUBMIT_FAILED:
				postStudentSubmitFailed(action)
				break
			case actionTypes.STUDENT_DRAW_IT_UNSUPPORTED_FILE_ERROR:
				postStudentDrawItUnsupportedFileError(action)
				break
			case actionTypes.STUDENT_TIMER_STARTED:
				postActivityTimerStarted(action)
				break
			case actionTypes.STUDENT_QUIZ_CHANGE_SLIDE:
			case actionTypes.STUDENT_SUBMIT_QUIZ_CANCEL:
				postQuizSlideChange(action)
				break
			case actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_ADD_LESSON_COMPLETED:
			case actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_SHOW_MY_LIBRARY:
			case actionTypes.DM_STORAGE_EXPERIMENT_GO_TO_MY_LIBRARY:
			case actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_GO_TO_MY_LIBRARY:
			case actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_GO_TO_MY_LIBRARY:
			case actionTypes.MY_LIBRARY_FIRST_LESSON_CLOSED:
			case actionTypes.MY_LIBRARY_FIRST_LESSON_COMPLETED:
			case actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_EMAIL_NOTIFICATION_ENABLED_SUCCESS:
			case actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_EMAIL_NOTIFICATION_ENABLED_FAILED:
			case actionTypes.NAVIGATION_MENU_MAIN_MANAGE_SELECTED:
				postMessage(action.type)
				break
			case actionTypes.MY_LIBRARY_EXTENSION_LESSON_LAUNCHED_SUCCESS:
				postExtensionSessionLaunched(action)
				break
			case actionTypes.GOOGLE_SLIDES_ACTIVITY_CREATE:
				postGoogleSlidesActivityCreate(action)
				break
			case actionTypes.GOOGLE_SLIDES_INITIALIZE_ACTIVITY_CANCEL:
				postGoogleSlidesInitializeActivityCancel(action)
				break
			case actionTypes.GOOGLE_SLIDES_INITIALIZE_ACTIVITY_SAVE:
				postGoogleSlidesInitializeActivitySave(action)
				break
			case actionTypes.GOOGLE_SLIDES_ACTIVITY_SAVED:
				postGoogleSlidesActivitySaved(action)
				break
			case actionTypes.GOOGLE_SLIDES_ACTIVITY_EDIT:
				postGoogleSlidesEditSlide()
				break
			case actionTypes.GOOGLE_SLIDES_GET_EXTERNAL_SLIDE_DATA_FOR_EDIT_SUCCESS:
				postGoogleSlidesEditSlideSuccess(action)
				break
			case actionTypes.GOOGLE_SLIDES_GET_EXTERNAL_SLIDE_DATA_FOR_EDIT_FAILED:
				postGetExternalSlideDataForEditFailed(action)
				break
			case actionTypes.GOOGLE_SLIDES_LEARN_MORE:
				postGoogleSlidesOpenModal(action)
				break
			case actionTypes.GOOGLE_SLIDES_SAVE_AND_GO_TO_NEARPOD:
				postInFrames(actionTypes.GOOGLE_SLIDES_SAVE_AND_GO_TO_NEARPOD, {}, 'top')
				break
			case actionTypes.GOOGLE_SLIDES_CLOSE_MODAL:
				postInFrames(actionTypes.GOOGLE_SLIDES_CLOSE_MODAL, {}, 'top')
				break
			case actionTypes.GOOGLE_SLIDES_INITIALIZE_SUCCESS:
				postGoogleSlidesInitializeSuccess(action)
				break
			case actionTypes.GOOGLE_SLIDES_INITIALIZE:
				postGoogleSlidesSideBarLoaded()
				break
			case actionTypes.LOGIN_INITIALIZE:
				postLogInLoadedInGoogleSlides(action)
				break
			case actionTypes.OEQ_INITIALIZE_STANDALONE:
			case actionTypes.DRAW_IT_INITIALIZE_STANDALONE:
			case actionTypes.QUIZ_INITIALIZE_STANDALONE:
			case actionTypes.POLL_INITIALIZE_STANDALONE:
			case actionTypes.GAME_INITIALIZE_STANDALONE:
			case actionTypes.MATCHING_PAIRS_INITIALIZE_STANDALONE:
			case actionTypes.FLOCABULARY_INITIALIZE_CREATION_TOOL_STANDALONE:
			case actionTypes.GOOGLE_SLIDES_LEARN_MORE_VIEW_INITIALIZE:
			case actionTypes.GOOGLE_SLIDES_CONFIRM_VIEW_INITIALIZE:
			case actionTypes.DESMOS_INITIALIZE_CREATION_TOOL_STANDALONE:
			case actionTypes.FLIPGRID_INITIALIZE_STANDALONE:
			case actionTypes.VIDEO_STANDALONE_INITIALIZE_EDIT_ROUTE:
			case actionTypes.VIDEO_STANDALONE_INITIALIZE_PICKER_ROUTE:
				postInFrames(actionTypes.GOOGLE_SLIDES_ACTIVITY_LOADED, {}, 'top')
				break
			case actionTypes.PROCESSING_ICON_SUCCESS:
				postActivityScreenshotObtained(store, action)
				break
			case actionTypes.GOOGLE_SLIDES_CONVERT_TO_DRAW_IT_GET_THUMBNAIL_SUCCESS:
				postConvertToDrawItGetThumbnailSuccess(action)
				break
			case actionTypes.SSO_SUCCESS_INITIALIZE:
				postSSOSuccess(action)
				break
			case actionTypes.STUDENT_LEAD_SUBMIT:
				postLeadSubmit(action)
				break
			case actionTypes.STUDENT_LEAD_SUBMIT_SUCCESS:
				postLeadSubmitSuccess(action)
				break
			case actionTypes.STUDENT_LEAD_SUBMIT_FAILED:
				postLeadSubmitFailed(action)
				break
			case actionTypes.READ_ONLY_NAVIGATE:
				handleReadOnlyNavigation(store, action)
				break
			case actionTypes.INTERACTIVE_VIDEO_IMMERSIVE_READER_MCQ_TRANSFORM:
			case actionTypes.INTERACTIVE_VIDEO_IMMERSIVE_READER_OEQ_TRANSFORM:
				postExternalImmersiveReaderChunks(store, action)
				break
			case actionTypes.JOIN_SUCCESS:
				postStudentJoinValidPincode(store, action)
				break
			case actionTypes.WHITEBOARD_ENABLE:
			case actionTypes.WHITEBOARD_DISABLE:
			case actionTypes.WHITEBOARD_ENABLED:
			case actionTypes.WHITEBOARD_DISABLED:
			case actionTypes.WHITEBOARD_FETCHED:
				postWhiteboardUpdate(action)
				break
			case actionTypes.STUDENT_INITIALIZE_LEAD_WITH_DATA:
				handleStudentInitializeLead(action)
				break
		}
	}
}

export default postMessageMiddleware
